const PREFS_KEY = "riverr.ask.prefs";
const OVERLAY_MAX = 1279;

export const ASK_WIDTH = { default: 380, wide: 520 };

export const ASK_SRC = {
  shop: { label: "shop", hint: "your orders, products, stock and payouts" },
  help: { label: "help", hint: "Guides and how-tos" },
  page: { label: "page", hint: "what you're looking at now" },
};

export const ASK_SUGGESTIONS = [];

const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/": "Dashboard",
  "/orders": "Orders",
  "/orders/:id": "this order",
  "/draft-orders": "Draft orders",
  "/unsynced-orders": "Unsynced orders",
  "/late-orders": "Late orders",
  "/products": "Products",
  "/products/blanks": "Blank products",
  "/shops": "Stores",
  "/settings": "Settings",
  "/settings/integrations": "Integrations",
  "/integrations": "Integrations",
  "/quotes": "Quotes",
  "/sales-orders": "Sales orders",
  "/customers": "Customers",
  "/inbox": "Inbox",
  "/pricing": "Billing",
  "/pos": "Register",
};

export const loadAskPrefs = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || "{}");
    return {
      open: raw.open === true,
      wide: raw.wide === true,
    };
  } catch (_) {
    return { open: false, wide: false };
  }
};

export const storeAskPrefs = (prefs) => {
  try {
    const prev = loadAskPrefs();
    localStorage.setItem(
      PREFS_KEY,
      JSON.stringify({ ...prev, ...prefs })
    );
  } catch (_) {
    /* ignore */
  }
};

export const isAskOverlayViewport = (width = window.innerWidth) =>
  Number(width) <= OVERLAY_MAX;

export const pageTitleOf = (pageContext) => {
  const route = pageContext?.route;
  if (!route) return "this page";
  if (PAGE_TITLES[route]) return PAGE_TITLES[route];
  const path = String(route).split("?")[0];
  if (PAGE_TITLES[path]) return PAGE_TITLES[path];
  const leaf = path.split("/").filter(Boolean).pop() || "this page";
  return leaf.replace(/-/g, " ");
};

export const questionForAlert = (type, extras = {}) => {
  if (type === "store_token_expired") {
    const store = extras.store || extras.shopName || "this store";
    return `Why did ${store} stop syncing?`;
  }
  if (type === "orders_late") {
    const n = extras.n ?? extras.count;
    return n != null ? `Why are ${n} orders late?` : "Why are orders late?";
  }
  if (type === "stock_low") return "What's low on stock?";
  return "";
};

const LATE_DAYS_KEY = "riverr.dashboard.lateOrdersDays";
const DEFAULT_LATE_DAYS = 3;

export const looksLikeLateQuestion = (value) =>
  /\b(late|overdue|behind)\b/i.test(String(value || ""));

export const scrubAskText = (value) =>
  String(value || "")
    .replace(/\bin Manowar\b/gi, "")
    .replace(/\bManowar\b/gi, "the team")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/[ \t]+([,.])/g, "$1")
    .trim();

export const readLateOrdersDays = () => {
  try {
    const parsed = Number(window.localStorage.getItem(LATE_DAYS_KEY));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_LATE_DAYS;
  } catch (_) {
    return DEFAULT_LATE_DAYS;
  }
};

const createdAtMs = (value) => {
  if (!value) return null;
  if (typeof value === "number") return value > 1e12 ? value : value * 1000;
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (typeof value._seconds === "number") return value._seconds * 1000;
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
};

const lateCustomerOf = (order = {}) =>
  order.customerName ||
  order.shippingAddress?.name ||
  order.billingAddress?.name ||
  order.customer?.name ||
  [order.customer?.firstName, order.customer?.lastName].filter(Boolean).join(" ") ||
  "customer";

const lateStatusOf = (order = {}) => {
  if (order.cancelled) return "cancelled";
  if (order.draft) return "draft";
  if (order.mapped === "unMapped") return "unsynced";
  if (order.shipped) return "shipped";
  return order.status || "production";
};

export const lateOrdersToAskResponse = (payload = {}, days = DEFAULT_LATE_DAYS) => {
  const orders = Array.isArray(payload?.orders) ? payload.orders : [];
  const counted = Number(payload?.totalCount);
  const total = Number.isFinite(counted) && counted >= 0 ? counted : orders.length;
  const now = Date.now();
  const rows = orders.slice(0, 5).map((order) => {
    const ms = createdAtMs(order.createdAt);
    const age = ms == null ? null : Math.floor((now - ms) / 86400000);
    const status = lateStatusOf(order);
    return {
      id: String(order.orderNumber || order.platformOrderId || order.id || "").slice(
        0,
        24
      ),
      label: lateCustomerOf(order),
      note: status,
      meta: age == null ? "" : `${age}d`,
      flag:
        status === "unsynced" || status === "cancelled"
          ? "red"
          : status === "draft" || status === "on_hold"
            ? "amber"
            : "none",
      href: order.id
        ? order.draft
          ? `/draft-orders/${order.id}`
          : `/orders/${order.id}`
        : "/late-orders",
    };
  });
  const noun = total === 1 ? "order is" : "orders are";
  const body =
    total === 0
      ? [`No open orders are older than ${days} days.`]
      : [
          `${total} open ${noun} past your ${days}-day threshold — still in production and not shipped.`,
          "The oldest are listed below. Open one to see what is blocking it, or review all late orders.",
        ];
  return {
    body,
    steps: [{ src: "shop", label: "Orders past your late threshold" }],
    rows: rows.length ? rows : undefined,
    rowsFrom: rows.length ? "Late orders" : undefined,
    rowsMore: rows.length ? Math.max(0, total - rows.length) : undefined,
    actions: [
      { label: "Open late orders", kind: "navigate", target: "/late-orders" },
    ],
  };
};

export const trackAsk = (event, props = {}) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...props });
  } catch (_) {
    /* ignore */
  }
};
